import React, { useState, useEffect, Suspense } from 'react';
import { Routes, Route } from 'react-router-dom';
import { collection, addDoc } from 'firebase/firestore';
import { db } from './firebase';
import './App.css';
import Stats from './pages/Stats';
import { TARGET_KM, TARGET_DATE_TIME, TARGET_DATE, START_DATE } from './constants';
import { useMobile } from './hooks/useMobile';
import { useCountdown } from './hooks/useCountdown';
import { useRuns } from './hooks/useRuns';
import Header from './components/Header';
import BeerProgress from './components/BeerProgress';
import WeeklyWidget from './components/WeeklyWidget';
import ProgressChart from './components/ProgressChart';
import TopRunners from './components/TopRunners';
import RunForm from './components/RunForm';
import RunsList from './components/RunsList';
import RunModal from './components/RunModal';
import CelebrationOverlay from './components/CelebrationOverlay';
import MilestoneNotification from './components/MilestoneNotification';

const isDevMode = process.env.REACT_APP_USE_DEV_DB === 'true';
const COLLECTION_NAME = isDevMode ? 'runs_dev' : 'runs_2';

function App() {
  const isMobile = useMobile();
  const countdown = useCountdown(TARGET_DATE_TIME);
  const isExpired = new Date() > TARGET_DATE_TIME;
  const {
    runs,
    totalKilometers,
    topRunners,
    existingNames,
    fetchRuns,
    submitRun,
    milestoneNotification,
    clearMilestoneNotification
  } = useRuns();

  const [showCelebration, setShowCelebration] = useState(false);
  const [selectedRun, setSelectedRun] = useState(null);

  useEffect(() => {
    fetchRuns();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (totalKilometers >= TARGET_KM && !showCelebration && !localStorage.getItem('celebrationShown')) {
      setShowCelebration(true);
      localStorage.setItem('celebrationShown', 'true');
    }
  }, [totalKilometers, showCelebration]);

  const seedTestData = async () => {
    if (!isDevMode) return;
    try {
      const testRunners = [
        { name: 'Test Bronze (48km)', kilometers: 48, date: '2024-12-01' },
        { name: 'Test Silver (99km)', kilometers: 99, date: '2024-12-02' },
        { name: 'Test Gold (199km)', kilometers: 199, date: '2024-12-03' },
      ];
      for (const runner of testRunners) {
        await addDoc(collection(db, COLLECTION_NAME), {
          ...runner,
          imageUrl: '',
          timestamp: new Date().toISOString()
        });
      }
      alert('Test data seeded! Runners are 1-2km away from milestones.');
      fetchRuns();
    } catch (error) {
      console.error('Error seeding test data:', error);
      alert('Failed to seed test data');
    }
  };

  const mainPage = (
    <div className="app">
      {isDevMode && (
        <div style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          backgroundColor: '#ff9800',
          color: 'white',
          padding: '8px 16px',
          borderRadius: '8px',
          fontSize: '0.9rem',
          fontWeight: 'bold',
          zIndex: 1000,
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)'
        }}>
          🔧 DEV MODE
        </div>
      )}

      <MilestoneNotification notification={milestoneNotification} onClose={clearMilestoneNotification} />

      {selectedRun && <RunModal run={selectedRun} onClose={() => setSelectedRun(null)} />}

      {showCelebration && (
        <CelebrationOverlay onClose={() => {
          setShowCelebration(false);
          localStorage.setItem('celebrationShown', 'true');
        }} />
      )}

      <Header />

      <BeerProgress totalKm={totalKilometers} targetKm={TARGET_KM} countdown={countdown} />

      <WeeklyWidget runs={runs} startDate={START_DATE} targetDate={TARGET_DATE} targetKm={TARGET_KM} />

      <div className="stats-container">
        <Suspense fallback={<div>Loading chart...</div>}>
          <ProgressChart
            runs={runs}
            startDate={START_DATE}
            targetDate={TARGET_DATE}
            targetKm={TARGET_KM}
            isMobile={isMobile}
          />
        </Suspense>
        <TopRunners topRunners={topRunners} />
      </div>

      <RunForm
        existingNames={existingNames}
        onSubmit={submitRun}
        isDevMode={isDevMode}
        onSeedTestData={seedTestData}
        isExpired={isExpired}
      />

      <RunsList runs={runs} onSelectRun={setSelectedRun} />
    </div>
  );

  return (
    <Routes>
      <Route path="/" element={mainPage} />
      <Route path="/stats" element={<Stats />} />
    </Routes>
  );
}

export default App;
